import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getReports } from '../services/reportService';
import { getGroups } from '../services/groupService';
import { getEmails } from '../services/emailService';
import { useTeam } from '../context/TeamContext';
import './Dashboard.css';

/**
 * Dashboard Page Component
 * 
 * Main landing page after successful authentication.
 * Fetches live stats from the API for reports, groups, and emails.
 * Stats are scoped to the active team and refetch when the team changes.
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const { activeTeamId } = useTeam();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalReports: 0,
    totalGroups: 0,
    totalEmails: 0,
    verifiedEmails: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch real data from all three endpoints in parallel
        const [reportsRes, groupsRes, emailsRes] = await Promise.all([
          getReports({ page: 1, limit: 100 }).catch(() => ({ data: [], pagination: { total: 0 } })),
          getGroups({ page: 1, limit: 100 }).catch(() => ({ data: [], pagination: { total: 0 } })),
          getEmails({ page: 1, limit: 100 }).catch(() => ({ data: [], pagination: { total: 0 } }))
        ]);

        const verifiedCount = emailsRes.summary
          ? emailsRes.summary.verified
          : emailsRes.data.filter(e => e.verificationStatus === 'verified').length;

        setStats({
          totalReports: reportsRes.pagination.total || reportsRes.data.length,
          totalGroups: groupsRes.pagination.total || groupsRes.data.length,
          totalEmails: emailsRes.summary?.total || emailsRes.pagination.total || emailsRes.data.length,
          verifiedEmails: verifiedCount
        });

        // Build recent activity from real reports and groups
        const activity = [
          ...reportsRes.data.map(r => ({
            type: 'report',
            action: 'updated',
            name: r.name,
            date: r.lastUpdated
          })),
          ...groupsRes.data.map(g => ({
            type: 'group',
            action: 'created',
            name: g.name,
            date: g.createdDate
          }))
        ]
          .filter(a => a.date)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 5);

        setRecentActivity(activity);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [activeTeamId]);

  if (loading) {
    return <LoadingSpinner fullscreen />;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-content">
        {/* Statistics Cards */}
        <div className="stats-grid">
          <Card className="stat-card" hoverable clickable onClick={() => navigate('/reports')}>
            <div className="stat-icon stat-icon-reports">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Reports</p>
              <p className="stat-value">{stats.totalReports}</p>
            </div>
          </Card>

          <Card className="stat-card" hoverable clickable onClick={() => navigate('/groups')}>
            <div className="stat-icon stat-icon-groups">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-label">Distribution Groups</p>
              <p className="stat-value">{stats.totalGroups}</p>
            </div>
          </Card>

          <Card className="stat-card" hoverable clickable onClick={() => navigate('/emails')}>
            <div className="stat-icon stat-icon-emails">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-label">Email Recipients</p>
              <p className="stat-value">{stats.totalEmails}</p>
              <p className="stat-detail">{stats.verifiedEmails} verified</p>
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-grid">
          {/* Recent Activity */}
          <Card className="activity-card">
            <h2 className="card-title">Recent Activity</h2>
            <div className="activity-list">
              {recentActivity.length > 0 ? (
                recentActivity.map((item, index) => (
                  <div key={index} className="activity-item">
                    <div className={`activity-icon activity-icon-${item.type}`}>
                      {item.type === 'report' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                        </svg>
                      )}
                    </div>
                    <div className="activity-details">
                      <p className="activity-text">
                        <span className="activity-name">{item.name}</span> was {item.action}
                      </p>
                      <p className="activity-date">{new Date(item.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-state">No recent activity</p>
              )}
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="actions-card">
            <h2 className="card-title">Quick Actions</h2>
            <div className="actions-list">
              <Button variant="secondary" fullWidth onClick={() => navigate('/my-reports')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                My Reports
              </Button>
              <Button variant="secondary" fullWidth onClick={() => navigate('/reports')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                View Reports
              </Button>
              <Button variant="secondary" fullWidth onClick={() => navigate('/groups')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
                Manage Groups
              </Button>
              <Button variant="secondary" fullWidth onClick={() => navigate('/emails')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Manage Emails
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
